// src/stores/dashboardStore.ts
// Zustand store for Operation Center dashboards

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  DashboardStore,
  UserDashboard,
  DashboardWidget,
  LayoutItem,
} from "@/types/dashboard";
import { getWidgetDefinition } from "@/components/operationCenter/widgets";
import {
  generateId,
  now,
  createDashboardFromTemplate,
} from "@/utils/operationCenter/templateUtils";
import {
  findBestPosition,
  computeSmartLayout,
  createLayoutItem,
  GRID_COLS,
  type WidgetDimensions,
} from "@/utils/operationCenter/layoutUtils";

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useDashboardStore = create<DashboardStore>()(
  persist(
    (set, get) => ({
      // Initial state
      dashboards: [],
      activeDashboardId: null,
      isEditing: false,
      isWidgetLibraryOpen: false,
      selectedWidgetId: null,
      isWidgetSettingsOpen: false,
      isAIAssistantOpen: false,
      isCreatingDashboard: false,
      isLoading: false,
      isSaving: false,
      lastAddedWidgetId: null,

      // ─────────────────────────────────────────────────────────────────────
      // Dashboard CRUD
      // ─────────────────────────────────────────────────────────────────────

      createDashboard: (name, template = "blank") => {
        const dashboard = createDashboardFromTemplate(name, template);
        set((state) => ({
          dashboards: [...state.dashboards, dashboard],
          activeDashboardId: dashboard.id,
          isCreatingDashboard: false,
        }));
        return dashboard;
      },

      deleteDashboard: (id) => {
        set((state) => {
          const newDashboards = state.dashboards.filter((d) => d.id !== id);
          const newActiveId =
            state.activeDashboardId === id
              ? newDashboards[0]?.id ?? null
              : state.activeDashboardId;
          return {
            dashboards: newDashboards,
            activeDashboardId: newActiveId,
          };
        });
      },

      duplicateDashboard: (id) => {
        const state = get();
        const original = state.dashboards.find((d) => d.id === id);
        if (!original) return null;

        // Create new widget IDs and update layout references
        const idMap = new Map<string, string>();
        const newWidgets = original.widgets.map((w) => {
          const newId = generateId("w");
          idMap.set(w.id, newId);
          return { ...w, id: newId };
        });

        const newLayout = original.layout.map((l) => {
          const newId = idMap.get(l.i) ?? l.i;
          const item: LayoutItem = {
            i: newId,
            x: l.x,
            y: l.y,
            w: l.w,
            h: l.h,
          };
          if (l.minW !== undefined) item.minW = l.minW;
          if (l.minH !== undefined) item.minH = l.minH;
          if (l.maxW !== undefined) item.maxW = l.maxW;
          if (l.maxH !== undefined) item.maxH = l.maxH;
          return item;
        });

        const duplicate: UserDashboard = {
          ...original,
          id: generateId("dash"),
          name: `${original.name} (Copy)`,
          widgets: newWidgets,
          layout: newLayout,
          created_at: now(),
          updated_at: now(),
          is_default: false,
        };

        set((state) => ({
          dashboards: [...state.dashboards, duplicate],
          activeDashboardId: duplicate.id,
        }));

        return duplicate;
      },

      renameDashboard: (id, name) => {
        set((state) => ({
          dashboards: state.dashboards.map((d) =>
            d.id === id ? { ...d, name, updated_at: now() } : d
          ),
        }));
      },

      setActiveDashboard: (id) => {
        set({ activeDashboardId: id, isEditing: false });
      },

      setDefaultDashboard: (id) => {
        set((state) => ({
          dashboards: state.dashboards.map((d) => ({
            ...d,
            is_default: d.id === id,
          })),
        }));
      },

      // ─────────────────────────────────────────────────────────────────────
      // Widget CRUD
      // ─────────────────────────────────────────────────────────────────────

      addWidget: (type, config, title) => {
        const state = get();
        const dashboard = state.dashboards.find(
          (d) => d.id === state.activeDashboardId
        );
        if (!dashboard) return;

        const definition = getWidgetDefinition(type);
        if (!definition) return;

        const widgetId = generateId("w");
        const widget: DashboardWidget = {
          id: widgetId,
          type,
          title: title ?? definition.label,
          config: { ...definition.defaultConfig, ...config },
        };

        // Find best position for the widget
        const position = findBestPosition(
          dashboard.layout,
          definition.defaultSize.w,
          definition.defaultSize.h,
          GRID_COLS
        );

        const constraints: { minW?: number; minH?: number; maxW?: number; maxH?: number } = {
          minW: definition.minSize.w,
          minH: definition.minSize.h,
        };
        if (definition.maxSize?.w !== undefined) constraints.maxW = definition.maxSize.w;
        if (definition.maxSize?.h !== undefined) constraints.maxH = definition.maxSize.h;

        const layoutItem = createLayoutItem(
          widgetId,
          position,
          definition.defaultSize,
          constraints
        );

        set((state) => ({
          dashboards: state.dashboards.map((d) =>
            d.id === state.activeDashboardId
              ? {
                  ...d,
                  widgets: [...d.widgets, widget],
                  layout: [...d.layout, layoutItem],
                  updated_at: now(),
                }
              : d
          ),
          isWidgetLibraryOpen: false,
          lastAddedWidgetId: widgetId,
        }));
      },

      addWidgetToDashboard: (dashboardId, type, config, title) => {
        const state = get();
        const dashboard = state.dashboards.find((d) => d.id === dashboardId);
        if (!dashboard) return;

        const definition = getWidgetDefinition(type);
        if (!definition) return;

        const widgetId = generateId("w");
        const widget: DashboardWidget = {
          id: widgetId,
          type,
          title: title ?? definition.label,
          config: { ...definition.defaultConfig, ...config },
        };

        const position = findBestPosition(
          dashboard.layout,
          definition.defaultSize.w,
          definition.defaultSize.h,
          GRID_COLS
        );

        const constraints: { minW?: number; minH?: number; maxW?: number; maxH?: number } = {
          minW: definition.minSize.w,
          minH: definition.minSize.h,
        };
        if (definition.maxSize?.w !== undefined) constraints.maxW = definition.maxSize.w;
        if (definition.maxSize?.h !== undefined) constraints.maxH = definition.maxSize.h;

        const layoutItem = createLayoutItem(
          widgetId,
          position,
          definition.defaultSize,
          constraints
        );

        set((state) => ({
          dashboards: state.dashboards.map((d) =>
            d.id === dashboardId
              ? {
                  ...d,
                  widgets: [...d.widgets, widget],
                  layout: [...d.layout, layoutItem],
                  updated_at: now(),
                }
              : d
          ),
          lastAddedWidgetId: widgetId,
        }));
      },

      removeWidget: (widgetId) => {
        set((state) => ({
          dashboards: state.dashboards.map((d) =>
            d.id === state.activeDashboardId
              ? {
                  ...d,
                  widgets: d.widgets.filter((w) => w.id !== widgetId),
                  layout: d.layout.filter((l) => l.i !== widgetId),
                  updated_at: now(),
                }
              : d
          ),
          selectedWidgetId:
            state.selectedWidgetId === widgetId ? null : state.selectedWidgetId,
        }));
      },

      updateWidgetConfig: (widgetId, config) => {
        set((state) => ({
          dashboards: state.dashboards.map((d) =>
            d.id === state.activeDashboardId
              ? {
                  ...d,
                  widgets: d.widgets.map((w) =>
                    w.id === widgetId
                      ? { ...w, config: { ...w.config, ...config } }
                      : w
                  ),
                  updated_at: now(),
                }
              : d
          ),
        }));
      },

      updateWidgetTitle: (widgetId, title) => {
        set((state) => ({
          dashboards: state.dashboards.map((d) =>
            d.id === state.activeDashboardId
              ? {
                  ...d,
                  widgets: d.widgets.map((w) =>
                    w.id === widgetId ? { ...w, title } : w
                  ),
                  updated_at: now(),
                }
              : d
          ),
        }));
      },

      // ─────────────────────────────────────────────────────────────────────
      // Layout
      // ─────────────────────────────────────────────────────────────────────

      updateLayout: (layout) => {
        set((state) => ({
          dashboards: state.dashboards.map((d) =>
            d.id === state.activeDashboardId
              ? { ...d, layout, updated_at: now() }
              : d
          ),
        }));
      },

      reLayoutDashboard: () => {
        const state = get();
        const dashboard = state.dashboards.find(
          (d) => d.id === state.activeDashboardId
        );
        if (!dashboard || dashboard.widgets.length === 0) return;

        // Build widget dimensions from current layout + definitions
        const widgetSizes: WidgetDimensions[] = dashboard.widgets.map(
          (widget) => {
            const definition = getWidgetDefinition(widget.type);
            const currentLayout = dashboard.layout.find(
              (l) => l.i === widget.id
            );

            const size: WidgetDimensions = {
              i: widget.id,
              w: currentLayout?.w ?? definition?.defaultSize.w ?? 4,
              h: currentLayout?.h ?? definition?.defaultSize.h ?? 3,
            };

            const minW = currentLayout?.minW ?? definition?.minSize.w;
            const minH = currentLayout?.minH ?? definition?.minSize.h;
            const maxW = currentLayout?.maxW ?? definition?.maxSize?.w;
            const maxH = currentLayout?.maxH ?? definition?.maxSize?.h;

            if (minW !== undefined) size.minW = minW;
            if (minH !== undefined) size.minH = minH;
            if (maxW !== undefined) size.maxW = maxW;
            if (maxH !== undefined) size.maxH = maxH;

            return size;
          }
        );

        const newLayout = computeSmartLayout(widgetSizes, GRID_COLS);

        set((state) => ({
          dashboards: state.dashboards.map((d) =>
            d.id === state.activeDashboardId
              ? { ...d, layout: newLayout, updated_at: now() }
              : d
          ),
        }));
      },

      // ─────────────────────────────────────────────────────────────────────
      // UI Actions
      // ─────────────────────────────────────────────────────────────────────

      setEditing: (isEditing) => {
        set({ isEditing });
      },

      setWidgetLibraryOpen: (isOpen) => {
        set({ isWidgetLibraryOpen: isOpen });
      },

      selectWidget: (widgetId) => {
        set({ selectedWidgetId: widgetId });
      },

      setWidgetSettingsOpen: (isOpen) => {
        set({ isWidgetSettingsOpen: isOpen });
      },

      setAIAssistantOpen: (isOpen) => {
        set({ isAIAssistantOpen: isOpen });
      },

      setCreatingDashboard: (isCreating) => {
        set({ isCreatingDashboard: isCreating });
      },

      // ─────────────────────────────────────────────────────────────────────
      // Thesis Association
      // ─────────────────────────────────────────────────────────────────────

      setDashboardThesis: (dashboardId, thesisId) => {
        set((state) => ({
          dashboards: state.dashboards.map((d) =>
            d.id === dashboardId
              ? { ...d, thesisId: thesisId ?? undefined, updated_at: now() }
              : d
          ),
        }));
      },

      // ─────────────────────────────────────────────────────────────────────
      // AI Widget Actions
      // ─────────────────────────────────────────────────────────────────────

      addAIWidget: (type, config, title, aiGenerated = true) => {
        const state = get();
        const dashboard = state.dashboards.find(
          (d) => d.id === state.activeDashboardId
        );
        if (!dashboard) return;

        const definition = getWidgetDefinition(type);
        if (!definition) return;

        const widgetId = generateId("w");
        const widget: DashboardWidget = {
          id: widgetId,
          type,
          title: title ?? definition.label,
          config: { ...definition.defaultConfig, ...config },
          ai_generated: aiGenerated,
        };

        const position = findBestPosition(
          dashboard.layout,
          definition.defaultSize.w,
          definition.defaultSize.h,
          GRID_COLS
        );

        const aiConstraints: { minW?: number; minH?: number; maxW?: number; maxH?: number } = {
          minW: definition.minSize.w,
          minH: definition.minSize.h,
        };
        if (definition.maxSize?.w !== undefined) aiConstraints.maxW = definition.maxSize.w;
        if (definition.maxSize?.h !== undefined) aiConstraints.maxH = definition.maxSize.h;

        const layoutItem = createLayoutItem(
          widgetId,
          position,
          definition.defaultSize,
          aiConstraints
        );

        set((state) => ({
          dashboards: state.dashboards.map((d) =>
            d.id === state.activeDashboardId
              ? {
                  ...d,
                  widgets: [...d.widgets, widget],
                  layout: [...d.layout, layoutItem],
                  updated_at: now(),
                }
              : d
          ),
          lastAddedWidgetId: widgetId,
        }));
      },

      clearLastAddedWidgetId: () => {
        set({ lastAddedWidgetId: null });
      },

      /**
       * Add multiple widgets at once with optimized layout.
       * Uses computeSmartLayout for better visual arrangement.
       */
      addWidgetsBatch: (widgets, dashboardId) => {
        const state = get();
        const targetId = dashboardId ?? state.activeDashboardId;
        const dashboard = state.dashboards.find((d) => d.id === targetId);
        if (!dashboard) return;

        // Build new widgets and their dimensions
        const newWidgets: DashboardWidget[] = [];
        const allDimensions: WidgetDimensions[] = [];

        // Include existing widgets in layout computation
        for (const existing of dashboard.widgets) {
          const def = getWidgetDefinition(existing.type);
          const existingLayout = dashboard.layout.find((l) => l.i === existing.id);
          const dims: WidgetDimensions = {
            i: existing.id,
            w: existingLayout?.w ?? def?.defaultSize.w ?? 4,
            h: existingLayout?.h ?? def?.defaultSize.h ?? 3,
          };
          if (def?.minSize.w) dims.minW = def.minSize.w;
          if (def?.minSize.h) dims.minH = def.minSize.h;
          if (def?.maxSize?.w) dims.maxW = def.maxSize.w;
          if (def?.maxSize?.h) dims.maxH = def.maxSize.h;
          allDimensions.push(dims);
        }

        // Create new widgets
        for (const w of widgets) {
          const definition = getWidgetDefinition(w.type);
          if (!definition) continue;

          const widgetId = generateId("w");
          const widget: DashboardWidget = {
            id: widgetId,
            type: w.type,
            title: w.title ?? definition.label,
            config: { ...definition.defaultConfig, ...w.config },
            ...(w.aiGenerated !== undefined && { ai_generated: w.aiGenerated }),
          };
          newWidgets.push(widget);

          // Add dimensions for layout computation
          const dims: WidgetDimensions = {
            i: widgetId,
            w: definition.defaultSize.w,
            h: definition.defaultSize.h,
          };
          if (definition.minSize.w) dims.minW = definition.minSize.w;
          if (definition.minSize.h) dims.minH = definition.minSize.h;
          if (definition.maxSize?.w) dims.maxW = definition.maxSize.w;
          if (definition.maxSize?.h) dims.maxH = definition.maxSize.h;
          allDimensions.push(dims);
        }

        // Compute optimized layout for all widgets (existing + new)
        const newLayout = computeSmartLayout(allDimensions, GRID_COLS);

        set((state) => ({
          dashboards: state.dashboards.map((d) =>
            d.id === targetId
              ? {
                  ...d,
                  widgets: [...d.widgets, ...newWidgets],
                  layout: newLayout,
                  updated_at: now(),
                }
              : d
          ),
          lastAddedWidgetId: newWidgets[newWidgets.length - 1]?.id ?? null,
        }));
      },

      // ─────────────────────────────────────────────────────────────────────
      // Persistence (handled by middleware)
      // ─────────────────────────────────────────────────────────────────────

      loadFromStorage: () => {
        // Handled by persist middleware
      },

      saveToStorage: () => {
        // Handled by persist middleware
      },
    }),
    {
      name: "abi-dashboards",
      partialize: (state) => ({
        dashboards: state.dashboards,
        activeDashboardId: state.activeDashboardId,
      }),
    }
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// Selectors (kept for backwards compatibility)
// ─────────────────────────────────────────────────────────────────────────────

export const useActiveDashboard = () => {
  const dashboards = useDashboardStore((s) => s.dashboards);
  const activeDashboardId = useDashboardStore((s) => s.activeDashboardId);
  return dashboards.find((d) => d.id === activeDashboardId) ?? null;
};

export const useActiveWidgets = () => {
  const dashboard = useActiveDashboard();
  return dashboard?.widgets ?? [];
};

export const useActiveLayout = () => {
  const dashboard = useActiveDashboard();
  return dashboard?.layout ?? [];
};

export const useWidget = (widgetId: string) => {
  const widgets = useActiveWidgets();
  return widgets.find((w) => w.id === widgetId) ?? null;
};

export const useLayoutItem = (widgetId: string) => {
  const layout = useActiveLayout();
  return layout.find((l) => l.i === widgetId) ?? null;
};
